/**
 * AccesosPage — gestión de la whitelist `public.allowed_emails`.
 *
 * Acceso: solo super-admins (los 3 emails hardcoded en useIsSuperAdmin +
 * is_super_admin() server-side). El sidebar oculta el ítem para los demás
 * y RLS bloquea el SELECT/INSERT/UPDATE/DELETE para no super-admins.
 *
 * Funcionalidad:
 *   - Listar autorizados (separados aosenuma | cliente)
 *   - Agregar email con scope + rol_app + tenant_slug
 *   - Suspender / reactivar
 *   - Eliminar (no permite borrarse a sí mismo)
 *
 * NO usa el BFF para esto — las RPCs corren con SECURITY DEFINER y RLS,
 * que ya autoriza por (auth.uid() ∈ super-admins). Más simple y misma seguridad.
 */
import { useEffect, useState, useMemo } from 'react';
import {
  getSupabase,
  isSupabaseConfigured,
  useIsSuperAdmin,
  isSuperAdminEmail,
  useUser,
  bffFetch,
  type AllowedEmailRow,
  type EmailScope,
  type EmailRol,
} from '@aosenuma/lib-supabase';
import { showToast } from '@aosenuma/ui';

const ROLES_AOSENUMA: EmailRol[] = ['admin_aosenuma', 'tecnico_aosenuma', 'auditor'];
const ROLES_CLIENTE: EmailRol[] = ['cliente_elawan'];

interface AddFormState {
  email: string;
  scope: EmailScope;
  rol_app: EmailRol;
  tenant_slug: string;
  notas: string;
}

interface InviteCredentials {
  email: string;
  tempPassword: string;
  emailSent: boolean;
  emailError: string | null;
  /** true = el user ya existía y reseteamos su password; false = creado nuevo. */
  existed: boolean;
}

const INITIAL_FORM: AddFormState = {
  email: '',
  scope: 'aosenuma',
  rol_app: 'tecnico_aosenuma',
  tenant_slug: '',
  notas: '',
};

export function AccesosPage() {
  const isSuperAdmin = useIsSuperAdmin();
  const { user } = useUser();
  const myEmail = user?.email?.toLowerCase().trim() ?? '';

  const [rows, setRows] = useState<AllowedEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<AddFormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Modal con la temp password recién emitida (post invite). Solo se muestra
   * una vez por invitación — el admin la copia y la comparte por canal seguro
   * (correo si llegó, WhatsApp/in-person si Resend falló o el filtro
   * corporativo bloquea). NUNCA persistir en localStorage ni state remoto.
   */
  const [credModal, setCredModal] = useState<InviteCredentials | null>(null);

  async function loadAll() {
    if (!isSupabaseConfigured()) return;
    setLoading(true);
    setError(null);
    try {
      const sb = getSupabase();
      const { data, error: qErr } = await sb
        .from('allowed_emails')
        .select('email, scope, tenant_slug, status, rol_app, notas, totp_required, pii_access')
        .order('scope', { ascending: true })
        .order('email', { ascending: true });
      if (qErr) throw qErr;
      setRows((data ?? []) as AllowedEmailRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isSuperAdmin) return;
    void loadAll();
  }, [isSuperAdmin]);

  // El AuthGate ya bloquea no-super-admins en /admin/accesos a través del
  // sidebar que oculta el link. Pero alguien con la URL directa entra aquí
  // y vería un fetch vacío. Mostramos forbidden defensivamente.
  if (!isSuperAdmin) {
    return (
      <section className="panel">
        <header className="panel-header">
          <h1>Sin permisos</h1>
          <p className="panel-sub">Esta sección es solo para super-admins.</p>
        </header>
      </section>
    );
  }

  function onScopeChange(scope: EmailScope) {
    setForm((f) => ({
      ...f,
      scope,
      rol_app: scope === 'aosenuma' ? 'tecnico_aosenuma' : 'cliente_elawan',
      tenant_slug: scope === 'cliente' ? (f.tenant_slug || 'elawan') : '',
    }));
  }

  /**
   * Llama al BFF /v1/admin/invite. El endpoint usa admin.createUser/updateUserById
   * para emitir contraseña temporal y la envía por Resend. Devuelve la temp
   * password en el response para que el admin la pueda compartir manualmente
   * cuando el correo no llega (filtros corporativos, dominio nuevo sin
   * reputación). El admin caller ya es super-admin con acceso DB total —
   * devolverla en JSON sobre TLS no añade superficie nueva.
   */
  async function sendInvite(email: string): Promise<InviteCredentials> {
    const res = await bffFetch<{
      ok: boolean;
      email: string;
      scope: EmailScope;
      existed: boolean;
      temp_password: string;
      email_sent: boolean;
      email_error: string | null;
    }>('/v1/admin/invite', {
      method: 'POST',
      body: { email },
    });
    return {
      email: res.email,
      tempPassword: res.temp_password,
      emailSent: res.email_sent,
      emailError: res.email_error,
      existed: res.existed,
    };
  }

  async function handleResendInvite(row: AllowedEmailRow) {
    if (!window.confirm(`¿Regenerar la contraseña temporal y reenviar invitación a ${row.email}? Cualquier contraseña previa quedará invalidada.`)) return;
    try {
      const creds = await sendInvite(row.email);
      setCredModal(creds);
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), { variant: 'error' });
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      showToast('Email inválido', { variant: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const sb = getSupabase();
      const { error: rpcErr } = await sb.rpc('allowed_email_upsert', {
        p_email: email,
        p_scope: form.scope,
        p_tenant_slug: form.scope === 'cliente' ? form.tenant_slug.trim() : null,
        p_rol_app: form.rol_app,
        p_notas: form.notas.trim() || null,
      });
      if (rpcErr) throw rpcErr;
      // Generar temp password + enviar correo (best-effort). Si Resend falla,
      // el modal igual muestra la contraseña para que el admin la comparta
      // por otro canal — el user en auth.users ya quedó creado.
      try {
        const creds = await sendInvite(email);
        setCredModal(creds);
      } catch (otpErr) {
        showToast(`${email} autorizado pero la generación de invitación falló: ${otpErr instanceof Error ? otpErr.message : 'error'}. Usa "Invitar" en la fila.`, { variant: 'info' });
      }
      setForm(INITIAL_FORM);
      await loadAll();
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSetStatus(email: string, status: 'active' | 'suspended') {
    const verbo = status === 'suspended' ? 'suspender' : 'reactivar';
    if (!window.confirm(`¿${verbo} acceso para ${email}?`)) return;
    try {
      const sb = getSupabase();
      const { error: rpcErr } = await sb.rpc('allowed_email_set_status', {
        p_email: email,
        p_status: status,
      });
      if (rpcErr) throw rpcErr;
      showToast(`${email} ${status === 'suspended' ? 'suspendido' : 'reactivado'}`);
      await loadAll();
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), { variant: 'error' });
    }
  }

  async function handleDelete(email: string) {
    if (email === myEmail) {
      showToast('No puedes eliminarte a ti mismo', { variant: 'error' });
      return;
    }
    if (!window.confirm(`¿Eliminar permanentemente a ${email}? Esta acción no se puede deshacer.`)) return;
    try {
      const sb = getSupabase();
      const { error: rpcErr } = await sb.rpc('allowed_email_delete', { p_email: email });
      if (rpcErr) throw rpcErr;
      showToast(`${email} eliminado`);
      await loadAll();
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), { variant: 'error' });
    }
  }

  /**
   * Togglea totp_required / pii_access de un correo vía RPC allowed_email_set_flags
   * (super-admin only + audita). El RPC exige AMBOS flags, así que tomamos el
   * patch sobre el row actual. Confirma solo al DESACTIVAR (baja seguridad).
   */
  async function handleSetFlags(
    row: AllowedEmailRow,
    patch: { totp_required?: boolean; pii_access?: boolean },
  ) {
    const nextTotp = patch.totp_required ?? row.totp_required;
    const nextPii = patch.pii_access ?? row.pii_access;
    const turningOff =
      (patch.totp_required === false) || (patch.pii_access === false);
    if (turningOff) {
      const que = patch.totp_required === false ? 'TOTP (entrará sin 2FA)' : 'acceso a PII';
      if (!window.confirm(`¿Desactivar ${que} para ${row.email}?`)) return;
    }
    try {
      const sb = getSupabase();
      const { error: rpcErr } = await sb.rpc('allowed_email_set_flags', {
        p_email: row.email,
        p_totp_required: nextTotp,
        p_pii_access: nextPii,
      });
      if (rpcErr) throw rpcErr;
      showToast(`Accesos actualizados para ${row.email}`);
      await loadAll();
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), { variant: 'error' });
    }
  }

  const aosenumaRows = useMemo(() => rows.filter((r) => r.scope === 'aosenuma'), [rows]);
  const clienteRows = useMemo(() => rows.filter((r) => r.scope === 'cliente'), [rows]);

  return (
    <section className="panel">
      <header className="panel-header">
        <h1>Accesos · whitelist</h1>
        <p className="panel-sub">
          Correos autorizados a la plataforma. Aosenuma → equipo · Cliente → su panel (slug.aosenumareport.com).
        </p>
      </header>

      <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Form: agregar */}
        <form onSubmit={handleAdd} className="glass-panel" style={{ padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 180px 160px auto', gap: 10, alignItems: 'end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-dim)' }}>Email</span>
              <input
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="usuario@dominio.com"
                style={inputStyle}
                required
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-dim)' }}>Tipo</span>
              <select value={form.scope} onChange={(e) => onScopeChange(e.target.value as EmailScope)} style={inputStyle}>
                <option value="aosenuma">Aosenuma (equipo)</option>
                <option value="cliente">Cliente</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-dim)' }}>Rol</span>
              <select
                value={form.rol_app}
                onChange={(e) => setForm((f) => ({ ...f, rol_app: e.target.value as EmailRol }))}
                style={inputStyle}
              >
                {(form.scope === 'aosenuma' ? ROLES_AOSENUMA : ROLES_CLIENTE).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ color: 'var(--text-dim)' }}>Tenant slug</span>
              <input
                value={form.tenant_slug}
                onChange={(e) => setForm((f) => ({ ...f, tenant_slug: e.target.value }))}
                placeholder={form.scope === 'cliente' ? 'elawan' : '—'}
                disabled={form.scope === 'aosenuma'}
                style={inputStyle}
              />
            </label>
            <button type="submit" className="btn-primary" disabled={submitting} style={{ height: 36 }}>
              {submitting ? 'Agregando…' : 'Agregar'}
            </button>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, marginTop: 10 }}>
            <span style={{ color: 'var(--text-dim)' }}>Notas (opcional)</span>
            <input
              value={form.notas}
              onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
              placeholder="Razón / responsable"
              style={inputStyle}
            />
          </label>
        </form>

        {/* Lista */}
        {loading ? (
          <div className="empty">Cargando…</div>
        ) : error ? (
          <div className="empty" style={{ color: 'var(--st-rojo, #c33)' }}>Error: {error}</div>
        ) : (
          <>
            <AccesosTable
              titulo={`Equipo Aosenuma (${aosenumaRows.length})`}
              rows={aosenumaRows}
              myEmail={myEmail}
              onSetStatus={handleSetStatus}
              onSetFlags={handleSetFlags}
              onDelete={handleDelete}
              onResendInvite={handleResendInvite}
            />
            <AccesosTable
              titulo={`Clientes (${clienteRows.length})`}
              rows={clienteRows}
              myEmail={myEmail}
              onSetStatus={handleSetStatus}
              onSetFlags={handleSetFlags}
              onDelete={handleDelete}
              onResendInvite={handleResendInvite}
            />
          </>
        )}
      </div>
      {credModal && (
        <InviteCredentialsModal
          creds={credModal}
          onClose={() => setCredModal(null)}
        />
      )}
    </section>
  );
}

interface AccesosTableProps {
  titulo: string;
  rows: AllowedEmailRow[];
  myEmail: string;
  onSetStatus: (email: string, status: 'active' | 'suspended') => void;
  onSetFlags: (row: AllowedEmailRow, patch: { totp_required?: boolean; pii_access?: boolean }) => void;
  onDelete: (email: string) => void;
  onResendInvite: (row: AllowedEmailRow) => void;
}

function AccesosTable({ titulo, rows, myEmail, onSetStatus, onSetFlags, onDelete, onResendInvite }: AccesosTableProps) {
  return (
    <div className="glass-panel" style={{ overflow: 'hidden' }}>
      <header style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', fontSize: 13, fontWeight: 600 }}>
        {titulo}
      </header>
      {rows.length === 0 ? (
        <div className="empty">— sin entradas —</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.7fr 0.9fr 0.8fr 92px 84px 184px', gap: 0 }}>
          <HeaderCell>Email</HeaderCell>
          <HeaderCell>Rol</HeaderCell>
          <HeaderCell>Tenant</HeaderCell>
          <HeaderCell>Notas</HeaderCell>
          <HeaderCell>Estado</HeaderCell>
          <HeaderCell>TOTP</HeaderCell>
          <HeaderCell>PII</HeaderCell>
          <HeaderCell>Acciones</HeaderCell>
          {rows.map((r) => (
            <RowCells
              key={r.email}
              row={r}
              isSelf={r.email === myEmail}
              onSetStatus={onSetStatus}
              onSetFlags={onSetFlags}
              onDelete={onDelete}
              onResendInvite={onResendInvite}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
      color: 'var(--text-dim)', borderBottom: '1px solid var(--glass-border)',
      background: 'rgba(255,255,255,0.02)',
    }}>{children}</div>
  );
}

interface FlagToggleProps {
  on: boolean;
  disabled?: boolean;
  title: string;
  labelOn: string;
  labelOff: string;
  onToggle: () => void;
}

/** Toggle pill (verde=on / gris=off). Usado para totp_required y pii_access. */
function FlagToggle({ on, disabled = false, title, labelOn, labelOff, onToggle }: FlagToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      title={title}
      aria-pressed={on}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 8px', borderRadius: 'var(--radius-pill)',
        fontSize: 11, fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: '1px solid var(--glass-border)',
        background: on ? 'rgba(74,156,165,0.18)' : 'rgba(255,255,255,0.04)',
        color: on ? 'var(--st-verde)' : 'var(--text-dim)',
        opacity: disabled ? 0.55 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: on ? 'var(--st-verde)' : 'var(--text-faint, #888)',
        flexShrink: 0,
      }} />
      {on ? labelOn : labelOff}
    </button>
  );
}

interface RowCellsProps {
  row: AllowedEmailRow;
  isSelf: boolean;
  onSetStatus: (email: string, status: 'active' | 'suspended') => void;
  onSetFlags: (row: AllowedEmailRow, patch: { totp_required?: boolean; pii_access?: boolean }) => void;
  onDelete: (email: string) => void;
  onResendInvite: (row: AllowedEmailRow) => void;
}

function RowCells({ row, isSelf, onSetStatus, onSetFlags, onDelete, onResendInvite }: RowCellsProps) {
  const cell: React.CSSProperties = {
    padding: '10px 12px', fontSize: 12.5, borderBottom: '1px solid var(--glass-border)',
    display: 'flex', alignItems: 'center',
  };
  // Piso de seguridad: los super-admins SIEMPRE requieren TOTP — su toggle
  // queda bloqueado (el RPC también lo rechaza server-side).
  const totpLocked = isSuperAdminEmail(row.email);
  return (
    <>
      <div style={cell}>
        {row.email}
        {isSelf && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-dim)' }}>(tú)</span>}
      </div>
      <div style={cell}>{row.rol_app}</div>
      <div style={cell}>{row.tenant_slug ?? '—'}</div>
      <div style={{ ...cell, color: 'var(--text-mid)' }}>{row.notas ?? '—'}</div>
      <div style={cell}>
        <span style={{
          padding: '2px 8px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 500,
          background: row.status === 'active' ? 'rgba(74,156,165,0.18)' : 'rgba(192,57,43,0.15)',
          color: row.status === 'active' ? 'var(--st-verde)' : 'var(--st-rojo, #c33)',
        }}>
          {row.status === 'active' ? 'activo' : 'suspendido'}
        </span>
      </div>
      <div style={cell}>
        <FlagToggle
          on={row.totp_required}
          disabled={totpLocked}
          title={totpLocked
            ? 'Super-admin: TOTP siempre obligatorio'
            : row.totp_required ? 'TOTP obligatorio — clic para desactivar' : 'Sin TOTP — clic para exigirlo'}
          labelOn="TOTP"
          labelOff="Sin TOTP"
          onToggle={() => onSetFlags(row, { totp_required: !row.totp_required })}
        />
      </div>
      <div style={cell}>
        <FlagToggle
          on={row.pii_access}
          title={row.pii_access ? 'Ve PII — clic para bloquear' : 'Sin PII — clic para permitir'}
          labelOn="PII"
          labelOff="Sin PII"
          onToggle={() => onSetFlags(row, { pii_access: !row.pii_access })}
        />
      </div>
      <div style={{ ...cell, gap: 6, flexWrap: 'wrap' }}>
        {row.status === 'active' ? (
          <button onClick={() => onSetStatus(row.email, 'suspended')} style={btnSm}>
            Suspender
          </button>
        ) : (
          <button onClick={() => onSetStatus(row.email, 'active')} style={btnSm}>
            Reactivar
          </button>
        )}
        <button
          onClick={() => onResendInvite(row)}
          disabled={row.status !== 'active'}
          title={row.status === 'active' ? 'Enviar magic link al correo' : 'Reactiva el acceso primero'}
          style={btnSm}
        >
          ✉️ Invitar
        </button>
        <button
          onClick={() => onDelete(row.email)}
          disabled={isSelf}
          title={isSelf ? 'No puedes eliminarte a ti mismo' : 'Eliminar permanentemente'}
          style={{ ...btnSm, color: isSelf ? 'var(--text-faint)' : 'var(--st-rojo, #c33)', cursor: isSelf ? 'not-allowed' : 'pointer' }}
        >
          ✕
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// InviteCredentialsModal — muestra email + temp password tras un alta o
// "Reenviar invitación" exitoso. Diseñado para que el admin copie ambos
// campos rápidamente (click → portapapeles + feedback visual) y los pegue en
// WhatsApp/Slack/in-person cuando el correo Resend no llega al destinatario
// (filtros corporativos, allowlist pendiente). La password se muestra UNA
// SOLA VEZ — no se vuelve a renderizar al recargar; si el admin la pierde,
// debe usar "Reenviar invitación" para regenerar otra.
// ---------------------------------------------------------------------------
interface InviteCredentialsModalProps {
  creds: InviteCredentials;
  onClose: () => void;
}

function InviteCredentialsModal({ creds, onClose }: InviteCredentialsModalProps) {
  const [copiedField, setCopiedField] = useState<'email' | 'password' | 'both' | null>(null);

  async function copy(value: string, field: 'email' | 'password' | 'both') {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField((f) => (f === field ? null : f)), 2200);
    } catch {
      // En contextos http sin permiso de clipboard, fallback a selección.
      showToast('No se pudo copiar — selecciona y copia manualmente.', { variant: 'info' });
    }
  }

  const bothBlock = `Correo:     ${creds.email}\nContraseña: ${creds.tempPassword}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-cred-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', zIndex: 9999,
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--bg-deep, #0c1216)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 12,
          padding: 24,
          width: 'min(520px, 100%)',
          color: 'var(--text)',
          fontFamily: 'inherit',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        <h2 id="invite-cred-title" style={{ margin: 0, fontSize: 19 }}>
          {creds.existed ? 'Contraseña temporal regenerada' : 'Usuario creado'}
        </h2>
        <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.5 }}>
          {creds.emailSent ? (
            <>
              Enviamos el correo a <strong>{creds.email}</strong> con las credenciales.
              Si no llega (filtro corporativo, spam), copia abajo y compártelas por
              otro canal seguro.
            </>
          ) : (
            <>
              <span style={{ color: '#e8a86e' }}>⚠ El correo no se pudo enviar</span>
              {creds.emailError ? <> ({creds.emailError})</> : null}.
              Comparte la contraseña por otro canal (WhatsApp, Slack, en persona).
            </>
          )}
        </p>

        <CredentialRow
          label="Correo"
          value={creds.email}
          onCopy={() => void copy(creds.email, 'email')}
          copied={copiedField === 'email'}
        />
        <CredentialRow
          label="Contraseña temporal"
          value={creds.tempPassword}
          onCopy={() => void copy(creds.tempPassword, 'password')}
          copied={copiedField === 'password'}
          mono
        />

        <div style={{
          marginTop: 4,
          padding: '10px 12px',
          background: 'rgba(232, 168, 110, 0.10)',
          border: '1px solid rgba(232, 168, 110, 0.30)',
          borderRadius: 6,
          fontSize: 12,
          color: 'var(--text-mid)',
          lineHeight: 1.5,
        }}>
          <strong style={{ color: '#e8a86e' }}>Esta contraseña solo se muestra una vez.</strong>{' '}
          No se guarda en ningún lado. Si la pierdes, regenera otra con
          "Invitar" en la fila correspondiente — la previa quedará invalidada.
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => void copy(bothBlock, 'both')}
            style={{
              flex: 1,
              padding: '10px 14px',
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--glass-border)',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: 13,
            }}
          >
            {copiedField === 'both' ? '✓ Copiado · pega en WA' : 'Copiar correo + contraseña'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-primary"
            style={{ padding: '10px 18px' }}
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}

interface CredentialRowProps {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
  mono?: boolean;
}

function CredentialRow({ label, value, onCopy, copied, mono = false }: CredentialRowProps) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <span style={{
        fontSize: 11,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--text-dim)',
      }}>{label}</span>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid var(--glass-border)',
        borderRadius: 6,
      }}>
        <code style={{
          flex: 1,
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit',
          fontSize: mono ? 14 : 13,
          color: 'var(--text)',
          wordBreak: 'break-all',
          userSelect: 'all',
          letterSpacing: mono ? '0.5px' : 'normal',
        }}>{value}</code>
        <button
          type="button"
          onClick={onCopy}
          aria-label={copied ? 'Copiado' : `Copiar ${label.toLowerCase()}`}
          title={copied ? 'Copiado' : 'Copiar al portapapeles'}
          style={{
            padding: '4px 10px',
            background: copied ? 'rgba(180,201,111,0.18)' : 'transparent',
            color: copied ? 'var(--btn-primary-bg, #b4c96f)' : 'var(--text)',
            border: `1px solid ${copied ? 'rgba(180,201,111,0.42)' : 'var(--glass-border)'}`,
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 11.5,
            fontWeight: 500,
            transition: 'background 0.15s, color 0.15s, border-color 0.15s',
            flexShrink: 0,
          }}
        >
          {copied ? '✓ Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  height: 36,
  padding: '0 10px',
  borderRadius: 6,
  border: '1px solid var(--glass-border)',
  background: 'rgba(255,255,255,0.04)',
  color: 'var(--text)',
  fontSize: 13,
};

const btnSm: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: 11,
  borderRadius: 4,
  border: '1px solid var(--glass-border)',
  background: 'transparent',
  cursor: 'pointer',
};
